<template>
  <div v-if="!shouldHideItemPrices" class="wc-product-price-v2-card" :class="platformModifier">
    <div
      v-if="hasActualPrice && !isAdditionalScanCode"
      class="wc-product-price-v2-card__actual"
      :class="{
        'wc-product-price-v2-card__actual--sale': isSaleWithoutBuyDown,
        'wc-product-price-v2-card__actual--sale-avg-weight':
          isSoldByAverageWeight && isSaleWithoutBuyDown,
      }"
      :aria-describedby="`${itemRef.id}-priceDesc`"
    >
      {{ actualPriceDisplay }}
      <template v-if="isSoldByAverageWeight">
        <span :class="{ 'wc-product-price-v2-card__sale-estimate': isSaleWithoutBuyDown }">
          {{ estimatedEachDisplay }}
        </span>
        <span
          ref="avgWeightIcon"
          class="wc-product-price-v2-card__avg-icon"
          :class="{ 'wc-product-price-v2-card__avg-icon--sale': isSaleWithoutBuyDown }"
        >
          <WCToolTip
            target="avgWeightIcon"
            position="top"
            class="wc-product-price-v2-card__avg-tooltip"
          >
            <div>{{ t('priceMayVaryWhenWeighed') }}</div>
            <div>{{ avgWeightTooltipEstimate }}</div>
          </WCToolTip>
          <WCSvgIcon name="circle-info" :icon-size="{ widthRem: 0.75, heightRem: 0.75 }" />
        </span>
        <span class="sr-only" :id="`${itemRef.id}-avgPriceText`">{{
          t('averagePerUnitPrice')
        }}</span>
      </template>
      <span class="sr-only" :id="`${itemRef.id}-priceDesc`">
        {{ isDiscounted ? t('salePrice') : t('productPrice') }}
      </span>
    </div>

    <div v-if="isAdditionalScanCode" class="wc-product-price-v2-card__see-in-cart">
      {{ t('seePriceInCart') }}
    </div>

    <div
      v-if="hasActualPrice && isSaleWithoutBuyDown && !isAdditionalScanCode"
      class="wc-product-price-v2-card__strikethrough"
      :aria-describedby="`${itemRef.id}-priceText`"
    >
      <s>{{ suggestedPriceDisplay }}</s>
      <span class="sr-only" :id="`${itemRef.id}-priceText`">{{ t('productPrice') }}</span>
    </div>

    <div
      v-if="hasActualPrice && !isAdditionalScanCode && hasBuyDown"
      class="wc-product-price-v2-card__in-cart-discount"
    >
      {{ t('discountAppliedInCartPdp') }}
    </div>

    <div
      v-if="!hasActualPrice && !isAdditionalScanCode"
      class="wc-product-price-v2-card__call-for-price"
    >
      {{ t('callForPrice') }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { defineProps, computed } from 'vue';
import ItemSummary from '@/models/entities/items/item-summary';
import useItemPrice from '@/composables/useItemPrice';
import usePlatform from '@/modules/platform/composables/usePlatform';
import WCToolTip from '@/components/WCToolTip/WCToolTip.vue';
import WCSvgIcon from '@/components/WCSvgIcon/WCSvgIcon.vue';
import useHidePricing from '@/composables/useHidePricing';
import { useI18n } from 'vue-i18n';

const props = defineProps<{
  item: ItemSummary;
}>();

const itemRef = computed(() => props.item);
const { t } = useI18n();

const {
  isAdditionalScanCode,
  hasActualPrice,
  isSoldByAverageWeight,
  isDiscounted,
  hasBuyDown,
  actualPriceDisplay,
  suggestedPriceDisplay,
  estimatedEachDisplay,
  avgWeightTooltipEstimate,
} = useItemPrice(itemRef);

const { shouldHideItemPrices } = useHidePricing();

const { platform } = usePlatform();

const isSaleWithoutBuyDown = computed(() => isDiscounted.value && !hasBuyDown.value);

const platformModifier = computed(() =>
  platform.value.isMobile
    ? 'wc-product-price-v2-card--mobile'
    : 'wc-product-price-v2-card--desktop',
);
</script>

<style scoped lang="scss">
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.wc-product-price-v2-card {
  margin-bottom: convert-px-to-rem(6);

  &--desktop {
    margin-bottom: convert-px-to-rem(8);
  }

  &__actual {
    display: inline-block;
    font-style: normal;
    font-weight: 700;
    font-size: $font-size-lg;
    line-height: convert-px-to-rem(22);
  }

  &__actual--sale {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    height: convert-px-to-rem(30);
    background-color: var(--primary, $primary);
    color: $white;
    border-radius: convert-px-to-rem(8);
    padding: 0 convert-px-to-rem(8);
    font-weight: 700;
    font-size: $font-size-lg;
  }

  &__actual--sale-avg-weight {
    gap: 0.38rem;
    justify-content: flex-start;
    flex-wrap: wrap;
    width: 100%;
    padding: 0.25rem 0.5rem;
  }

  &__strikethrough {
    color: var(--dark, $dark);
    display: inline-block;
    vertical-align: top;
    margin-left: $pad-2;
    font-style: normal;
    font-weight: 400;
    font-size: $font-size-sm;
    line-height: convert-px-to-rem(17);
  }

  &__see-in-cart,
  &__call-for-price {
    display: block;
    font-style: normal;
    font-weight: 700;
    font-size: $font-size-sm;
    line-height: normal;
    color: $black;
  }

  &__avg-icon :deep(.wc-icon) {
    color: $black;
  }

  &--mobile &__avg-icon :deep(.wc-icon) {
    vertical-align: middle;
  }

  &__avg-icon--sale {
    display: inline-flex;
    align-items: center;

    :deep(.wc-icon) {
      color: $white;
      vertical-align: middle;
    }
  }

  &__in-cart-discount {
    display: block;
    font-weight: 700;
    font-style: normal;
    line-height: normal;
    font-size: $font-size-sm;
    margin-top: 0.25rem;
  }

  &__avg-tooltip :deep(.tooltip-inner) {
    border-radius: 0.3125rem;
    padding: 0.63rem 0.69rem 0.69rem 0.63rem;
    font-size: 0.75rem;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    text-align: left;
  }

  &__avg-tooltip :deep(.tooltip-inner > div + div) {
    margin-top: 0.375rem;
  }

  &__avg-tooltip :deep(.arrow),
  &__avg-tooltip :deep(.arrow)::before {
    left: 0;
  }

  &--mobile &__avg-tooltip :deep(.tooltip-inner) {
    padding: 0.5rem 0.65rem 0.5rem 0.65rem;
    font-size: 0.75rem;
  }

  &--desktop &__actual {
    font-size: $font-size-xl;
    line-height: convert-px-to-rem(24);
  }

  &--desktop &__actual--sale {
    height: auto;
    background: none;
    color: var(--primary, $primary);
    padding: 0;
    border-radius: 0;
  }

  &--desktop &__actual--sale-avg-weight {
    padding: 0;
    row-gap: convert-px-to-rem(2);
    line-height: convert-px-to-rem(22);
  }

  &--desktop &__avg-icon--sale :deep(.wc-icon) {
    color: var(--primary, $primary);
  }

  &--desktop &__strikethrough {
    font-size: convert-px-to-rem(13);
    line-height: convert-px-to-rem(16);
    color: var(--dark-lighter-5);
    margin-left: 0;
  }
}

.wc-product-price-v2-card--desktop:has(.wc-product-price-v2-card__actual--sale):not(:has(.wc-product-price-v2-card__actual--sale-avg-weight)) {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: baseline;
  column-gap: convert-px-to-rem(6);
  row-gap: 0;
}

.wc-product-price-v2-card--desktop:has(.wc-product-price-v2-card__actual--sale-avg-weight) {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  row-gap: convert-px-to-rem(2);
}
</style>
